import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Apple, Download, Share, Plus, Smartphone, ArrowRight } from 'lucide-react';
import {
  appDownloads,
  detectPlatform,
  type DevicePlatform,
} from '../config/appDownloads';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface AppDownloadPageProps {
  /** Path to fall through to when the user chooses to keep using the browser. */
  continueTo: string;
}

/**
 * Install / download screen for the native apps, shown at the `/app` entry to
 * browser visitors who have not installed the app. Logged-in users, installed
 * PWAs, and the Capacitor native shell skip this screen (see AppEntry).
 */
const AppDownloadPage: React.FC<AppDownloadPageProps> = ({ continueTo }) => {
  const [platform] = useState<DevicePlatform>(() => detectPlatform());
  const [installPrompt, setInstallPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handlePwaInstall = async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    if (choice.outcome === 'accepted') setInstallPrompt(null);
  };

  const { androidApkUrl, playStoreUrl, iosAppStoreUrl } = appDownloads;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-gray-50 to-gray-100 px-6 py-12">
      <div className="w-full max-w-md text-center">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-black text-white shadow-lg">
          <Smartphone className="h-8 w-8" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900">Get the ChessDirector app</h1>
        <p className="mt-2 text-sm text-gray-600">
          Install the native app for faster access, offline support, and push
          notifications — or keep using it right here in your browser.
        </p>

        <div className="mt-8 space-y-3 text-left">
          {/* Android */}
          {platform !== 'ios' && (
            <>
              {playStoreUrl && (
                <a
                  href={playStoreUrl}
                  className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm transition hover:border-gray-300 hover:shadow"
                >
                  <Download className="h-5 w-5 text-green-600" />
                  <span className="flex-1">
                    <span className="block text-sm font-semibold text-gray-900">
                      Get it on Google Play
                    </span>
                    <span className="block text-xs text-gray-500">Android phones &amp; tablets</span>
                  </span>
                  <ArrowRight className="h-4 w-4 text-gray-400" />
                </a>
              )}
              {androidApkUrl && (
                <a
                  href={androidApkUrl}
                  className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm transition hover:border-gray-300 hover:shadow"
                >
                  <Download className="h-5 w-5 text-gray-700" />
                  <span className="flex-1">
                    <span className="block text-sm font-semibold text-gray-900">
                      Download Android APK
                    </span>
                    <span className="block text-xs text-gray-500">
                      Direct install (enable “install unknown apps”)
                    </span>
                  </span>
                  <ArrowRight className="h-4 w-4 text-gray-400" />
                </a>
              )}
            </>
          )}

          {/* iOS */}
          {platform !== 'android' && (
            <>
              {iosAppStoreUrl ? (
                <a
                  href={iosAppStoreUrl}
                  className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm transition hover:border-gray-300 hover:shadow"
                >
                  <Apple className="h-5 w-5 text-gray-900" />
                  <span className="flex-1">
                    <span className="block text-sm font-semibold text-gray-900">
                      Download on the App Store
                    </span>
                    <span className="block text-xs text-gray-500">iPhone &amp; iPad</span>
                  </span>
                  <ArrowRight className="h-4 w-4 text-gray-400" />
                </a>
              ) : (
                platform === 'ios' && (
                  <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-left shadow-sm">
                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                      <Apple className="h-5 w-5" /> Add to Home Screen
                    </div>
                    <ol className="mt-2 space-y-1 text-xs text-gray-600">
                      <li className="flex items-center gap-1">
                        1. Tap <Share className="inline h-3.5 w-3.5" /> Share in Safari
                      </li>
                      <li className="flex items-center gap-1">
                        2. Choose <Plus className="inline h-3.5 w-3.5" /> Add to Home Screen
                      </li>
                      <li>3. Open ChessDirector from your home screen</li>
                    </ol>
                  </div>
                )
              )}
            </>
          )}

          {/* Desktop / PWA install */}
          {installPrompt && (
            <button
              type="button"
              onClick={handlePwaInstall}
              className="flex w-full items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 text-left shadow-sm transition hover:border-gray-300 hover:shadow"
            >
              <Download className="h-5 w-5 text-blue-600" />
              <span className="flex-1">
                <span className="block text-sm font-semibold text-gray-900">
                  Install web app
                </span>
                <span className="block text-xs text-gray-500">Add to this device</span>
              </span>
              <ArrowRight className="h-4 w-4 text-gray-400" />
            </button>
          )}
        </div>

        <Link
          to={continueTo}
          replace
          className="mt-8 inline-block text-sm font-medium text-gray-500 underline-offset-4 hover:text-gray-900 hover:underline"
        >
          Continue in browser
        </Link>
      </div>
    </div>
  );
};

export default AppDownloadPage;
